import Input from 'component/atom/forms/input/input/Input';
import Select from 'component/atom/forms/select/select/Select';
import Modal from 'component/hoc/modal/Modal';
import { useFormik } from 'formik';
import { Agent } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import { usePost } from 'hooks/usePost';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import styles from './NewConnection.module.scss';
import { useToast } from 'app-context/toasterContext/ToastContext';
import Button from 'component/atom/forms/button/Button';
import CheckboxGroup from 'component/atom/forms/checkbox/CheckboxGroup';
import Loader from 'component/atom/loader/Loader';
import { isEmpty } from 'lodash';

const getValidationSchema = () =>
  Yup.object().shape({
    agentType: Yup.number().required('System Type is required'),
    connectionName: Yup.string()
      .required('Connection Name is required')
      .max(100, 'Connection Name cannot exceed 100 characters.'),
    clientId: Yup.string().when('agentType', {
      is: (val) => [1, 2, 3].includes(val),
      then: (schema) => schema.required('Client ID is required'),
    }),
    clientSecret: Yup.string().when('agentType', {
      is: (val) => [1, 2, 3].includes(val),
      then: (schema) => schema.required('Client Secret is required'),
    }),
    baseURl: Yup.string()
      .trim()
      .test(
        'is-valid-url',
        'Enter a valid URL (e.g. https://example.com)',
        function (value) {
          if (!value) return false;
          const v = value.trim();
          let urlToCheck = v;
          if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) urlToCheck = 'https://' + v;
          try {
            const url = new URL(urlToCheck);
            const hostname = url.hostname;
            if (!hostname || /\s/.test(hostname)) return false;
            const parts = hostname.split('.');
            if (parts.length < 2) return false;
            const tld = parts[parts.length - 1];
            if (!tld || /\s/.test(tld)) return false;
            if (/\/{2,}/.test(url.pathname)) return false;
            return true;
          } catch {
            return false;
          }
        }
      )
      .required('Enter a valid URL (e.g. https://example.com)'),
  });

const Form = ({ formik, isEdit }) => {
  const { data: agentTypesData } = useFetch(
    ['allAgentsTypes'],
    Agent.getAgentsTypes,
    { enable: true }
  );

  const showHidArray = [
    {
      name: 'connectionName',
      label: 'Connection Name',
      show: formik.values.agentType,
    },
    {
      name: 'clientId',
      label: 'Client ID',
      show:
        formik.values.agentType === 1 ||
        formik.values.agentType === 2 ||
        formik.values.agentType === 3,
    },
    {
      name: 'clientSecret',
      label: 'Client Secret',
      show:
        formik.values.agentType === 1 ||
        formik.values.agentType === 2 ||
        formik.values.agentType === 3,
    },
    {
      name: 'baseURl',
      label: 'Base URL',
      show:
        formik.values.agentType === 1 ||
        formik.values.agentType === 2 ||
        formik.values.agentType === 3,
    },
    {
      name: 'refreshToken',
      label: 'Refresh Token',
      show: formik.values.agentType === 2 || formik.values.agentType === 3,
    },
    { name: 'secretKey', label: 'Secret Key' },
  ];

  const options = agentTypesData?.data?.map((val) => ({
    label: val.agentName,
    value: val.id,
  }));

  return (
    <form
      autoComplete='off'
      onSubmit={formik.handleSubmit}
      className='formCont'
    >
      <Select
        label='System Type'
        placeholder='Select system type'
        options={options}
        name='agentType'
        value={formik.values.agentType}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.agentType}
        touched={formik.touched.agentType}
        classname={styles['daily-select']}
        selectClassname={styles.select}
        optionClassname={styles.whiteOptions}
        optionsClassName={styles.options}
        disabled={isEdit}
      />
      {showHidArray?.map((item) => {
        if (!item?.show) return null;
        const shouldDisable = isEdit && item.name !== 'connectionName';
        return (
          <Input
            key={item.name}
            label={item.label}
            placeholder={`Enter ${item.label}`}
            name={item.name}
            value={formik.values?.[item.name]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors?.[item.name]}
            touched={formik.touched?.[item.name]}
            inputContainerClassName={styles.connectionInput}
            disabled={shouldDisable}
          />
        );
      })}
    </form>
  );
};

const NewConnection = ({
  isOpen,
  onClose,
  botId,
  refetch,
  agentItem,
  setAgentItem,
}) => {
  const toast = useToast();
  const [agentConnectionDetailId, setAgentConnectionDetailId] = useState('');
  const [showSemesterModal, setShowSemesterModal] = useState(false);
  const [hideConnectionModal, setHideConnectionModal] = useState(false);
  const [selectedTermIds, setSelectedTermIds] = useState([]);
  const { mutate: PostAgent, isPending } = usePost();
  const { mutate: postTerms, isPending: isPendingTerms } = usePost();
  const isCreateInitialRef = useRef(!agentItem);
  useEffect(() => {
    if (isOpen && !showSemesterModal) {
      isCreateInitialRef.current = !agentItem;
    }
  }, [isOpen, agentItem, showSemesterModal]);

  const {
    data: semesters,
    refetch: refetchSemesters,
    isLoading: isLoadingSemesters,
    isFetching: isFetchingSemesters,
  } = useFetch(
    ['semesters', agentConnectionDetailId],
    Agent.getTerms(agentConnectionDetailId),
    { enabled: false }
  );

  const termOptions = useMemo(() => {
    const root = semesters?.data?.data ?? semesters?.data ?? {};
    const terms =
      Array.isArray(root.terms) && root.terms.length
        ? root.terms
        : root?.selectedData?.result?.items || [];
    return Array.isArray(terms)
      ? terms.map((t) => ({
          label: t.Name,
          value: String(t.Id),
        }))
      : [];
  }, [semesters, agentConnectionDetailId]);

  useEffect(() => {
    if (agentItem?.termId) setAgentConnectionDetailId(String(agentItem.termId));
  }, [agentItem?.termId]);

  useEffect(() => {
    if (!showSemesterModal || !agentConnectionDetailId) return;
    refetchSemesters().then((res) => {
      const root = res?.data?.data ?? res?.data ?? {};
      const items = root?.selectedData?.result?.items ?? [];

      if (!Array.isArray(items) || !items.length) {
        toast.open(
          'Credentials are invalid. Please enter the correct credentials.',
          'error'
        );
        return;
      }

      setSelectedTermIds(items.map((x) => String(x.Id)));
    });
  }, [showSemesterModal, agentConnectionDetailId, refetchSemesters]);

  useEffect(() => {
    const root = semesters?.data?.data ?? semesters?.data ?? {};
    const items = root?.selectedData?.result?.items ?? [];
    if (Array.isArray(items))
      setSelectedTermIds(items.map((x) => String(x.Id)));
  }, [semesters?.dataUpdatedAt]);

  const formik = useFormik({
    initialValues: {
      agentType: agentItem?.agentType || '',
      connectionName: agentItem?.agentName || '',
      clientId: agentItem?.clientId || '',
      clientSecret: agentItem?.clientSecret || '',
      baseURl: agentItem?.baseURL || '',
      refreshToken: agentItem?.refreshToken || '',
      secretKey: agentItem?.secretKey || '',
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      const payload = {
        botId: botId,
        agentType: Number(values.agentType),
        agentName: values.connectionName,
        clientId: values.clientId,
        clientSecret: values.clientSecret,
        baseURL: values.baseURl,
        refreshToken: values.refreshToken,
        isMasked: true,
      };
      if (!agentItem) {
        try {
          PostAgent(
            { data: payload, url: Agent.newConnection },
            {
              onSuccess: (e) => {
                if (!e?.data?.message?.isSuccess) {
                  toast.open(e?.data?.userMessage, 'error');
                  return;
                }
                setAgentItem(e?.data || null);
                setAgentConnectionDetailId(String(e?.data?.termId || ''));
                setHideConnectionModal(true);
                if (values.agentType !== 3) {
                  setShowSemesterModal(true);
                } else {
                  refetch();
                }
              },
            }
          );
        } catch {}
      } else {
        try {
          PostAgent(
            {
              data: { id: agentItem.id, ...payload },
              url: Agent.editConnection,
            },
            {
              onSuccess: (e) => {
                const id =
                  e?.data?.termId ??
                  agentItem?.termId ??
                  agentConnectionDetailId;
                if (id && !agentConnectionDetailId)
                  setAgentConnectionDetailId(String(id));
                setAgentItem(null);
                setHideConnectionModal(true);
                if (values.agentType !== 3) {
                  setShowSemesterModal(true);
                } else {
                  refetch();
                }
              },
            }
          );
        } catch {}
      }
    },
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (!isOpen && !showSemesterModal) {
      if (formik) formik.resetForm();
      setSelectedTermIds([]);
      setAgentConnectionDetailId('');
      setHideConnectionModal(false);
    }
  }, [isOpen, showSemesterModal]);

  return (
    <div>
      <Modal
        title={agentItem ? 'Edit Connection' : 'Create New Connection'}
        isOpen={isOpen && !hideConnectionModal}
        onClose={() => {
          setHideConnectionModal(false);
          setAgentItem(null);
          onClose();
        }}
        modalContent={{
          component: <Form formik={formik} isEdit={!!agentItem} />,
        }}
        onSubmit={() => {
          formik.handleSubmit();
        }}
        buttonType='submit'
        onSubmitstyles={{
          onSubmit: {
            label: 'Connect',
            opicBAckground: true,
          },
        }}
        isLoading={isPending}
        cancelClassName={styles.cancelBtn}
        cancelVariant='dark'
      />

      <Modal
        title='Select Semester'
        isOpen={showSemesterModal}
        onClose={() => {
          setShowSemesterModal(false);
          setHideConnectionModal(false);
          if (typeof refetch === 'function') refetch();
          setAgentItem(null);
          onClose();
        }}
        exitComponent={
          <Button
            label='Clear'
            variant=''
            onClick={() => setSelectedTermIds([])}
          />
        }
        modalContent={{
          component:
            isFetchingSemesters || isLoadingSemesters ? (
              <Loader />
            ) : (
              <CheckboxGroup
                key={`sem-${agentConnectionDetailId}-${termOptions.length}`}
                name='terms'
                options={termOptions}
                selectedValues={selectedTermIds}
                onChange={(vals) => setSelectedTermIds(vals)}
                className={styles['checkbox-container']}
                column
                hasTag={false}
                separators={true}
                labelClassName={styles['checkbox-label']}
              />
            ),
        }}
        footerComponent={<></>}
        isLoading={isPendingTerms}
        onSubmit={() => {
          if (!selectedTermIds.length) {
            toast.open('Select at least one semester', 'error');
            return;
          }
          postTerms(
            {
              data: selectedTermIds,
              url: Agent.saveTerms(agentConnectionDetailId),
            },
            {
              onSuccess: () => {
                setShowSemesterModal(false);
                setHideConnectionModal(false);
                if (typeof refetch === 'function') refetch();
                setAgentItem(null);
                onClose();
              },
            }
          );
        }}
        disabledFooter={isEmpty(selectedTermIds)}
        disabledCancel={
          isFetchingSemesters ||
          isLoadingSemesters ||
          selectedTermIds.length > 0
        }
      />
    </div>
  );
};

export default NewConnection;
